import { z } from "zod";

/**
 * US-IDM.F3-0523 (@UC9512) — GET /v1/users/search?q= — tìm người theo TÊN, TOÀN HỆ THỐNG.
 *
 * 🔴 NỚI một quyết định PO đã có: `/v1/users/lookup` (`users-identity.ts`) cố ý chỉ khớp CHÍNH XÁC
 * `publicHandle` — *"handle là danh tính công khai duy nhất chủ động"*, bạn chỉ tìm được người khác
 * qua thứ họ chủ động đưa cho bạn. QĐ người dùng 23–24/08/2026: mở thêm đường tìm theo TÊN, sau khi
 * đã cân nhắc rủi ro (một người đăng nhập gõ tên là duyệt được người dùng thật trong app tài chính
 * có trẻ em) và người dùng tái khẳng định muốn có tính năng này.
 *
 * ⇒ Đây là ENDPOINT MỚI, TÁCH KHỎI `/users/lookup` — không sửa route cũ, không đổi luật khớp-chính-
 * xác của nó. Giữ nguyên MỌI lớp chống quét danh bạ mà lookup đã có: rate-limit chặt, audit-on-read
 * mỗi lượt (kể cả 0 kết quả), response tối giản (không email/phone), chỉ user `active` + chưa xoá.
 *
 * `groupId` CỐ Ý KHÔNG có ở query. Endpoint này KHÔNG biết và KHÔNG cần biết ngữ cảnh nhóm nào đang
 * gọi nó — "người này đã ở trong nhóm X chưa" là việc của CLIENT, đối chiếu với danh sách thành viên
 * nó đã tải sẵn cho nhóm đó. Nhét `groupId` vào đây biến một API tìm-người-toàn-hệ-thống thành một
 * API vừa tìm vừa biết ngữ cảnh nhóm — hai việc, một endpoint, khó suy luận khi RBAC nhóm đổi.
 *
 * 🔴 ĐẶT TÊN `globalUserSearch*`, KHÔNG PHẢI `userSearch*`. `search.ts` (US-E13.F0-0082-S27) đã
 * chiếm `userSearchQuerySchema`/`USER_SEARCH_MAX_LIMIT` cho một khái niệm KHÁC HẲN: tìm NỘI DUNG
 * (giao dịch/thành viên/quỹ) trong phạm vi membership của người gọi — "search AS a user". Tính năng
 * ở đây là tìm MỘT NGƯỜI, không giới hạn membership — "search FOR a user". Trùng tiền tố là ngẫu
 * nhiên, không phải cùng cơ chế; dùng chung tên sẽ khiến `tsc` từ chối biên dịch (đã đo: `TS2308
 * đã export trùng tên`) và người đọc sau này tưởng nhầm là một API.
 */

/** Tối thiểu 2 ký tự — gõ 1 ký tự khớp gần nửa hệ thống, không phải một phép tìm kiếm có ích. */
export const GLOBAL_USER_SEARCH_QUERY_MIN_LENGTH = 2;
export const GLOBAL_USER_SEARCH_QUERY_MAX_LENGTH = 100;

/** Trần cứng 20 kết quả/trang — đủ cho việc chọn người bằng mắt, không mời quét toàn bảng. */
export const GLOBAL_USER_SEARCH_MAX_LIMIT = 20;
export const GLOBAL_USER_SEARCH_DEFAULT_LIMIT = 20;

/**
 * 🔴 Cùng con số 512 với MỌI cursor khác trong repo (`INVITE_LINKS_MAX_CURSOR_LENGTH`,
 * `GROUP_PEOPLE_MAX_CURSOR_LENGTH`, …) — quy ước chung, không phải đo riêng cho endpoint này.
 * `users-search-cursor.ts` (`apps/api`) tự khai một `MAX_CURSOR_LENGTH` NỘI BỘ cùng giá trị —
 * ĐÚNG quy ước sẵn có của MỌI file `*.cursor.ts` khác trong `apps/api/src/modules`
 * (defense-in-depth CỐ Ý: biên contract chặn trước, decoder tự chặn lại — hai hằng số RIÊNG,
 * không import chung một nguồn). Thiếu `.max()` ở ĐÂY thì cursor khổng lồ vẫn mở+rollback trọn
 * một transaction audit trước khi bị service từ chối — lãng phí đúng cái mà lớp validate ở biên
 * (`.strict()`) tồn tại để tránh.
 */
export const GLOBAL_USER_SEARCH_MAX_CURSOR_LENGTH = 512;

export const globalUserSearchQuerySchema = z
  .object({
    q: z
      .string()
      .trim()
      .min(GLOBAL_USER_SEARCH_QUERY_MIN_LENGTH)
      .max(GLOBAL_USER_SEARCH_QUERY_MAX_LENGTH),
    cursor: z.string().max(GLOBAL_USER_SEARCH_MAX_CURSOR_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(GLOBAL_USER_SEARCH_MAX_LIMIT).optional(),
  })
  .strict();

export type GlobalUserSearchQuery = z.infer<typeof globalUserSearchQuerySchema>;

/**
 * Một kết quả tìm kiếm — TỐI GIẢN như `userLookupResponseSchema`, cùng lý do C2/C3 (PII):
 * KHÔNG email/phone/status/role. `publicHandle` giúp phân biệt hai người trùng tên.
 *
 * 🔴 `publicHandle: null` là giá trị HỢP LỆ, không phải lỗi thiếu dữ liệu. Cột nullable ở DB — chỉ
 * sinh khi user vào nhóm parent hoặc tự mở màn Hồ sơ. QĐ người dùng: KHÔNG sinh handle như một tác
 * dụng phụ của việc NGƯỜI KHÁC tìm kiếm — đó là ghi vào hồ sơ của một người vì một hành động của
 * người khác, không phải của chính họ.
 */
export const globalUserSearchItemSchema = z
  .object({
    userId: z.string().uuid(),
    displayName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
    publicHandle: z.string().nullable(),
  })
  .strict();

export type GlobalUserSearchItem = z.infer<typeof globalUserSearchItemSchema>;

export const globalUserSearchResponseSchema = z
  .object({
    data: z.array(globalUserSearchItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        /** Opaque — mã hoá (điểm khớp, userId) của phần tử cuối trang. Client chuyển tiếp nguyên văn. */
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();

export type GlobalUserSearchResponse = z.infer<typeof globalUserSearchResponseSchema>;
